<!--
  HelpDeskFAQs — Pineapple 톤
  ────────────────────────────────────────
  · FAQ 목록 + 카테고리/상태 필터 + 아코디언 펼침
  · 모달 폼은 노출되지 않으나 데이터/메서드는 보존
-->
<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="p-page">
    <a-page-head title="자주 묻는 질문" :breadcrumbs="[{ label: '헬프데스크' }, { label: 'FAQ' }]">
    </a-page-head>

    <!-- KPI -->
    <div class="p-grid p-grid--kpi">
      <a-card padding="20"><a-stat label="전체 FAQ" :value="String(faqs.length)" /></a-card>
      <a-card padding="20"><a-stat label="활성 FAQ" :value="String(activeFaqs)" /></a-card>
      <a-card padding="20"><a-stat label="카테고리" :value="String(categories.length)" /></a-card>
      <a-card padding="20"><a-stat label="이번 달 조회" :value="String(monthlyViews)" delta="+12%" delta-kind="ok" /></a-card>
    </div>

    <!-- 검색/필터 -->
    <a-card title="검색 & 필터" padding="16">
      <div class="p-filter-row">
        <div class="p-field p-field--wide">
          <label class="p-field__label">검색어</label>
          <input class="p-input" type="text" v-model="searchQuery" @input="filterFaqs" placeholder="질문이나 답변 검색..." />
        </div>
        <div class="p-field">
          <label class="p-field__label">카테고리</label>
          <select class="p-input" v-model="selectedCategory" @change="filterFaqs">
            <option value="">전체</option>
            <option v-for="c in categories" :key="c.value" :value="c.value">{{ $t(c.labelKey) }}</option>
          </select>
        </div>
        <div class="p-field">
          <label class="p-field__label">상태</label>
          <select class="p-input" v-model="statusFilter" @change="filterFaqs">
            <option value="">전체</option>
            <option value="active">활성</option>
            <option value="inactive">비활성</option>
          </select>
        </div>
      </div>
    </a-card>

    <!-- 아코디언 -->
    <a-card title="FAQ 목록" :sub="`${filteredFaqs.length}건`" padding="0" class="p-mt-16">
      <div v-if="loading" class="p-loader"><div class="p-spinner" /></div>
      <a-empty v-else-if="!filteredFaqs.length" title="검색 결과가 없습니다" />
      <div v-else class="p-accordion">
        <div
          v-for="faq in paginatedFaqs"
          :key="faq.id"
          class="p-accordion__item"
          :class="{ 'is-open': expandedFaqId === faq.id, 'is-inactive': !faq.is_active }"
        >
          <button class="p-accordion__head" @click="toggleAccordion(faq.id)">
            <a-pill kind="yellow">{{ getCategoryLabel(faq.category) }}</a-pill>
            <span class="p-accordion__q">{{ faq.question }}</span>
            <a-pill :kind="faq.is_active ? 'ok' : 'default'">{{ faq.is_active ? '활성' : '비활성' }}</a-pill>
            <span class="p-accordion__views">👁 {{ faq.views || 0 }}</span>
            <svg class="p-accordion__chev" :class="{ 'is-open': expandedFaqId === faq.id }" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          <div v-if="expandedFaqId === faq.id" class="p-accordion__body">
            <div class="p-accordion__answer" v-html="faq.answer" />
            <div class="p-accordion__meta">
              <span>최종 수정: {{ formatDate(faq.updated_at) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="filteredFaqs.length > itemsPerPage" class="p-pagination">
        <button class="p-page-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">‹</button>
        <button
          v-for="p in totalPages"
          :key="p"
          class="p-page-btn"
          :class="{ 'is-active': currentPage === p }"
          @click="changePage(p)"
        >{{ p }}</button>
        <button class="p-page-btn" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">›</button>
      </div>
    </a-card>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import APageHead from '@/components/Pineapple/APageHead.vue';
import ACard from '@/components/Pineapple/ACard.vue';
import AStat from '@/components/Pineapple/AStat.vue';
import APill from '@/components/Pineapple/APill.vue';
import AEmpty from '@/components/Pineapple/AEmpty.vue';

export default {
  name: 'HelpDeskFAQs',
  components: { APageHead, ACard, AStat, APill, AEmpty },
  data() {
    return {
      faqs: [],
      filteredFaqs: [],
      categories: [
        { value: 'general',   labelKey: 'inquiry.helpdesk_category.general' },
        { value: 'payment',   labelKey: 'inquiry.helpdesk_category.payment' },
        { value: 'technical', labelKey: 'inquiry.helpdesk_category.technical' },
        { value: 'account',   labelKey: 'inquiry.helpdesk_category.account' },
        { value: 'course',    labelKey: 'inquiry.helpdesk_category.course' },
        { value: 'other',     labelKey: 'inquiry.helpdesk_category.other' },
      ],
      loading: false,
      searchQuery: '',
      selectedCategory: '',
      statusFilter: '',
      expandedFaqId: null,
      currentPage: 1,
      itemsPerPage: 10,
      editingFaq: null,
      formData: { category: '', question: '', answer: '', sort_order: 0, is_active: true },
    };
  },
  computed: {
    activeFaqs()  { return this.faqs.filter(f => f.is_active).length; },
    monthlyViews(){ return this.faqs.reduce((s, f) => s + (f.views || 0), 0); },
    totalPages()  { return Math.ceil(this.filteredFaqs.length / this.itemsPerPage); },
    paginatedFaqs() {
      const s = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredFaqs.slice(s, s + this.itemsPerPage);
    },
  },
  mounted() { this.loadFaqs(); },
  methods: {
    async loadFaqs() {
      this.loading = true;
      try {
        const r = await this.$axios({ method: 'get', url: '/helpdesk/faqs', loadingBar: true });
        this.faqs = r.data.faqs || [];
        this.filterFaqs();
      } catch (e) {
        console.error('FAQ 로딩 실패:', e);
        Swal.fire({ icon: 'error', title: '로딩 실패', text: 'FAQ 목록을 불러오는데 실패했습니다.' });
      } finally {
        this.loading = false;
      }
    },
    filterFaqs() {
      let filtered = [...this.faqs];
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        filtered = filtered.filter(f =>
          f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q),
        );
      }
      if (this.selectedCategory) filtered = filtered.filter(f => f.category === this.selectedCategory);
      if (this.statusFilter === 'active')   filtered = filtered.filter(f => f.is_active);
      if (this.statusFilter === 'inactive') filtered = filtered.filter(f => !f.is_active);
      filtered.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      this.filteredFaqs = filtered;
      this.currentPage = 1;
    },
    toggleAccordion(id) {
      this.expandedFaqId = this.expandedFaqId === id ? null : id;
    },
    changePage(p) {
      if (p >= 1 && p <= this.totalPages) this.currentPage = p;
    },
    formatDate(s) {
      if (!s) return '-';
      return new Date(s).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
    },
    getCategoryLabel(v) {
      const c = this.categories.find(x => x.value === v);
      return c ? this.$t(c.labelKey) : v;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_pineapple.scss';

.p-page {
  font-family: $p-font-body;
  color: $p-ink;
}
.p-mt-16 { margin-top: 16px; }

.p-grid {
  display: grid;
  gap: 16px;
  margin-bottom: 16px;

  &--kpi { grid-template-columns: repeat(4, 1fr); }
  @media (max-width: 1100px) { &--kpi { grid-template-columns: repeat(2, 1fr); } }
}

.p-filter-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 12px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
}
.p-field {
  display: flex; flex-direction: column; gap: 6px;
  &__label {
    font-size: 10px; font-weight: 800;
    letter-spacing: 0.1em; text-transform: uppercase;
    color: $p-ink-soft;
  }
}
.p-input {
  width: 100%; height: 36px;
  padding: 0 12px;
  border: 1px solid $p-line; border-radius: 8px;
  background: $p-panel; font-family: $p-font-body;
  font-size: 12px; color: $p-ink; outline: none;
  &:focus { border-color: $p-point; box-shadow: 0 0 0 3px rgba(255,201,60,0.2); }
}

.p-loader { padding: 60px 0; text-align: center; }
.p-spinner {
  display: inline-block;
  width: 36px; height: 36px;
  border: 3px solid $p-line; border-top-color: $p-point;
  border-radius: 50%; animation: p-spin 0.8s linear infinite;
}
@keyframes p-spin { to { transform: rotate(360deg); } }

.p-accordion {
  display: flex; flex-direction: column;

  &__item {
    border-bottom: 1px solid $p-line-soft;
    &:last-child { border-bottom: 0; }
    &.is-open { background: $p-panel-alt; }
    &.is-inactive .p-accordion__q { color: $p-ink-soft; }
  }

  &__head {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 16px 20px;
    border: 0;
    background: transparent;
    cursor: pointer;
    text-align: left;
    font-family: $p-font-body;

    &:hover { background: $p-bg; }
  }
  &__q {
    flex: 1;
    font-size: 13px;
    font-weight: 700;
    color: $p-ink;
  }
  &__views {
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-ink-soft;
  }
  &__chev {
    color: $p-ink-soft;
    transition: transform 0.2s ease;

    &.is-open { transform: rotate(180deg); color: $p-point-deep; }
  }
  &__body {
    padding: 0 20px 20px;
    background: $p-panel-alt;
  }
  &__answer {
    font-size: 13px;
    line-height: 1.7;
    color: $p-ink2;
    padding: 12px 16px;
    background: $p-panel;
    border-left: 3px solid $p-point;
    border-radius: 0 6px 6px 0;

    :deep(p) { margin: 0 0 8px; }
    :deep(p:last-child) { margin-bottom: 0; }
    :deep(a) { color: $p-point-deep; }
  }
  &__meta {
    margin-top: 10px;
    font-size: 11px;
    color: $p-ink-soft;
  }
}

.p-pagination {
  display: flex; justify-content: center;
  gap: 4px; padding: 16px;
  border-top: 1px solid $p-line;
}
.p-page-btn {
  min-width: 32px; height: 32px;
  padding: 0 8px;
  border: 1px solid $p-line; background: $p-panel;
  border-radius: 6px;
  font-family: $p-font-mono; font-size: 12px; color: $p-ink2;
  cursor: pointer;
  &:hover:not(:disabled) { background: $p-bg; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &.is-active { background: $p-ink; color: #fff; border-color: $p-ink; }
}
</style>
